import { Grid, TextField } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonWithLoader } from 'components/reusable/button-loader';
import { List } from 'components/reusable/list';
import { posthog } from 'posthog';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { checkKey, deleteKey, existKey, updateKey } from 'store/sagas/sagas';
import './source-key.css';
const captureKeyRecreated = (keyType, environment, accountGroupId) => {
    posthog.capture('api_key_recreated', {
        key_type: keyType,
        environment,
        is_account_group_key: accountGroupId !== null
    });
};
export const SourceKey = () => {
    const { state, dispatch, asyncDispatch } = useGlobalState();
    const [isLoading, setIsLoading] = useState(false);
    const [publicKey, setPublicKey] = useState(null);
    const [privateKey, setPrivateKey] = useState(null);
    const [signatureKey, setSignatureKey] = useState(null);
    const [groupKeys, setGroupKeys] = useState({});
    const [sandboxPublicKey, setSandboxPublicKey] = useState(null);
    const [sandboxPrivateKey, setSandboxPrivateKey] = useState(null);
    const nameRef = useRef(null);
    const [currentKey, setCurrentKey] = useState('');
    const [signatureKeyExists, setSignatureKeyExists] = useState(false);
    const [sandboxKeyExists, setSandboxKeyExists] = useState(false);
    const handleKey = useCallback((e) => {
        setCurrentKey(e.target.value);
    }, []);
    useEffect(() => {
        if (!state.user?.accountId) {
            return;
        }
        setIsLoading(false);
        Promise.all([
            asyncDispatch(existKey('signature')),
            asyncDispatch(existKey('sandbox'))
        ])
            .then(([signature, sandbox]) => {
            setSignatureKeyExists(signature.key);
            setSandboxKeyExists(sandbox.key);
        }, console.error)
            .finally(() => {
            setIsLoading(false);
        });
    }, []);
    const onCopyKey = (type, id) => () => {
        let copyData;
        if (id) {
            copyData = groupKeys[`${type}.${id}`] ?? '';
        }
        else {
            switch (type) {
                case 'public': {
                    copyData = publicKey;
                    break;
                }
                case 'private': {
                    copyData = privateKey;
                    break;
                }
                case 'signature': {
                    copyData = signatureKey;
                    break;
                }
                case 'sandboxPublic': {
                    copyData = sandboxPublicKey;
                    break;
                }
                case 'sandboxPrivate': {
                    copyData = sandboxPrivateKey;
                    break;
                }
                // No default
            }
        }
        if (!copyData) {
            return;
        }
        navigator.clipboard.writeText(copyData).catch((error) => {
            console.error('failed to write to clipboard', error);
        });
        dispatch(setMessages({ value: 'Copied!', severity: 'success' }));
    };
    const onUpdatePublicKey = (accountGroupId) => {
        setIsLoading(true);
        asyncDispatch(updateKey('public', accountGroupId))
            .then(({ key }) => {
            if (accountGroupId === null) {
                setPublicKey(key);
            }
            else {
                setGroupKeys((prev) => ({
                    ...prev,
                    [`public.${accountGroupId}`]: key
                }));
            }
            captureKeyRecreated('public', 'live', accountGroupId);
        }, console.error)
            .finally(() => {
            setIsLoading(false);
        });
    };
    const onUpdatePrivateKey = (accountGroupId) => {
        setIsLoading(true);
        asyncDispatch(updateKey('private', accountGroupId))
            .then(({ key }) => {
            if (accountGroupId === null) {
                setPrivateKey(key);
            }
            else {
                setGroupKeys((prev) => ({
                    ...prev,
                    [`private.${accountGroupId}`]: key
                }));
            }
            captureKeyRecreated('private', 'live', accountGroupId);
        }, console.error)
            .finally(() => {
            setIsLoading(false);
        });
    };
    const onUpdateSandboxKey = (accountGroupId) => {
        setIsLoading(true);
        asyncDispatch(updateKey('public', accountGroupId, 'sandbox'))
            .then(({ key }) => {
            if (accountGroupId === null) {
                setSandboxPublicKey(key);
            }
            else {
                setGroupKeys((prev) => ({
                    ...prev,
                    [`sandboxPublic.${accountGroupId}`]: key
                }));
            }
            captureKeyRecreated('public', 'sandbox', accountGroupId);
        }, console.error)
            .finally(() => {
            setIsLoading(false);
        });
    };
    const onUpdateSandboxPrivateKey = (accountGroupId) => {
        setIsLoading(true);
        asyncDispatch(updateKey('private', accountGroupId, 'sandbox'))
            .then(({ key }) => {
            if (accountGroupId === null) {
                setSandboxPrivateKey(key);
            }
            else {
                setGroupKeys((prev) => ({
                    ...prev,
                    [`sandboxPrivate.${accountGroupId}`]: key
                }));
            }
            captureKeyRecreated('private', 'sandbox', accountGroupId);
        }, console.error)
            .finally(() => {
            setIsLoading(false);
        });
    };
    const onUpdateSignatureKey = async (accountGroupId, action = 'create') => {
        setIsLoading(true);
        try {
            if (action === 'create') {
                await asyncDispatch(updateKey('signature', accountGroupId)).then(({ key }) => {
                    if (accountGroupId === null) {
                        setSignatureKey(key);
                    }
                    else {
                        setGroupKeys((prev) => ({
                            ...prev,
                            [`signature.${accountGroupId}`]: key
                        }));
                    }
                    captureKeyRecreated('signature', 'live', accountGroupId);
                });
                return;
            }
            await asyncDispatch(deleteKey('signature'));
        }
        catch (error) {
            // supress
            console.error(error);
        }
        finally {
            setIsLoading(false);
        }
    };
    const onUpdateKey = (type, accountGroupId, action = 'create') => {
        // eslint-disable-next-line no-alert
        if (!confirm(`Are you sure you want to ${action} this ${type} key?`)) {
            return;
        }
        switch (type) {
            case 'public':
                onUpdatePublicKey(accountGroupId);
                return;
            case 'signature':
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                onUpdateSignatureKey(accountGroupId, action);
                return;
            case 'private':
                onUpdatePrivateKey(accountGroupId);
        }
    };
    const onCheckKey = () => {
        asyncDispatch(checkKey(currentKey, null))
            .then(({ status }) => {
            dispatch(setMessages(status
                ? {
                    value: `Your key is valid`,
                    severity: 'success'
                }
                : {
                    value: `Your key is invalid`,
                    severity: 'error'
                }));
        })
            .catch(console.error);
    };
    return (React.createElement("div", { className: "source-main" },
        React.createElement("h1", { className: "g-page-name" }, "API Keys"),
        React.createElement("form", { autoComplete: "off", className: "g-form", noValidate: true },
            React.createElement("span", { className: "title" }, "Public Keys"),
            React.createElement("p", null, "Once recreated, the original keys will be gone. Please be certain."),
            React.createElement("div", { className: "g-form", style: {
                    width: 'calc(98% - 60px)',
                    height: '10px',
                    padding: '10px 30px 10px 30px'
                } }, publicKey && (React.createElement("div", { className: "pointer", onClick: onCopyKey('public') },
                React.createElement("i", { className: "fas fa-copy text-weak u-margin-right-large" }),
                React.createElement("span", { "data-ph-mask": true }, publicKey)))),
            React.createElement(ButtonWithLoader, { className: "u-margin-bottom-small", color: "secondary", disabled: false, isLoading: isLoading, name: "Recreate Public Key", onClick: () => {
                    onUpdateKey('public', null, 'create');
                } })),
        React.createElement("form", { autoComplete: "off", className: "g-form", noValidate: true },
            React.createElement("span", { className: "title" }, "Private Keys"),
            React.createElement("p", null, "Once recreated, the original keys will be gone. Please be certain."),
            React.createElement("div", { className: "g-form", style: {
                    width: 'calc(98% - 60px)',
                    height: '10px',
                    padding: '10px 30px 10px 30px'
                } }, privateKey && (React.createElement("div", { className: "pointer", onClick: onCopyKey('private') },
                React.createElement("i", { className: "fas fa-copy text-weak u-margin-right-large" }),
                React.createElement("span", { "data-ph-mask": true }, privateKey)))),
            React.createElement(ButtonWithLoader, { className: "u-margin-bottom-small", color: "secondary", disabled: false, isLoading: isLoading, name: "Recreate Private Key", onClick: () => {
                    onUpdateKey('private', null, 'create');
                } })),
        sandboxKeyExists && (React.createElement(React.Fragment, null,
            React.createElement("form", { autoComplete: "off", className: "g-form", noValidate: true },
                React.createElement("span", { className: "title" }, "Sandbox Public Keys"),
                React.createElement("p", null, "Once recreated, the original keys will be gone. Please be certain."),
                React.createElement("div", { className: "g-form", style: {
                        width: 'calc(98% - 60px)',
                        height: '10px',
                        padding: '10px 30px 10px 30px'
                    } }, sandboxPublicKey && (React.createElement("div", { className: "pointer", onClick: onCopyKey('sandboxPublic') },
                    React.createElement("i", { className: "fas fa-copy text-weak u-margin-right-large" }),
                    React.createElement("span", { "data-ph-mask": true }, sandboxPublicKey)))),
                React.createElement(ButtonWithLoader, { className: "u-margin-bottom-small", color: "secondary", disabled: false, isLoading: isLoading, name: "Recreate Sandbox Public Key", onClick: () => {
                        onUpdateSandboxKey(null);
                    } })),
            React.createElement("form", { autoComplete: "off", className: "g-form", noValidate: true },
                React.createElement("span", { className: "title" }, "Sandbox Private Keys"),
                React.createElement("p", null, "Once recreated, the original keys will be gone. Please be certain."),
                React.createElement("div", { className: "g-form", style: {
                        width: 'calc(98% - 60px)',
                        height: '10px',
                        padding: '10px 30px 10px 30px'
                    } }, sandboxPrivateKey && (React.createElement("div", { className: "pointer", onClick: onCopyKey('sandboxPrivate') },
                    React.createElement("i", { className: "fas fa-copy text-weak u-margin-right-large" }),
                    React.createElement("span", { "data-ph-mask": true }, sandboxPrivateKey)))),
                React.createElement(ButtonWithLoader, { className: "u-margin-bottom-small", color: "secondary", disabled: false, isLoading: isLoading, name: "Recreate Sandbox Private Key", onClick: () => {
                        onUpdateSandboxPrivateKey(null);
                    } })))),
        React.createElement("form", { autoComplete: "off", className: "g-form", noValidate: true },
            React.createElement("span", { className: "title" }, "Signature Keys"),
            React.createElement("p", null,
                React.createElement("a", { href: "https://docs.vouched.id/#section/Overview/Security-and-Privacy", rel: "noreferrer", target: "_blank" }, "This is a replacement for the private key"),
                ' ',
                "."),
            React.createElement("div", { className: "g-form", style: {
                    width: 'calc(98% - 60px)',
                    height: '10px',
                    padding: '10px 30px 10px 30px'
                } }, signatureKey && (React.createElement("div", { className: "pointer", onClick: onCopyKey('signature') },
                React.createElement("i", { className: "fas fa-copy text-weak u-margin-right-large" }),
                React.createElement("span", { "data-ph-mask": true }, signatureKey)))),
            React.createElement(ButtonWithLoader, { className: "u-margin-bottom-small", color: "secondary", disabled: false, isLoading: isLoading, name: "Recreate Signature Key", onClick: () => {
                    onUpdateKey('signature', null, 'create');
                } }),
            React.createElement(ButtonWithLoader, { className: "u-margin-bottom-small", color: "secondary", disabled: !signatureKeyExists, isLoading: isLoading, name: "Delete Signature Key", onClick: () => {
                    onUpdateKey('signature', null, 'delete');
                } })),
        React.createElement("form", { autoComplete: "off", className: "g-form", noValidate: true },
            React.createElement("span", { className: "title" }, "Check Key Status"),
            React.createElement("p", null, "Paste key in box to check if key is active."),
            React.createElement("div", { style: { display: 'flex' } },
                React.createElement(Grid, { container: true },
                    React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                        React.createElement(Grid, { item: true, xs: 6 },
                            React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", inputRef: nameRef, label: "Key", onChange: handleKey, required: true, style: { minWidth: 200 } }))))),
            React.createElement(ButtonWithLoader, { className: "u-margin-bottom-small", color: "secondary", disabled: false, isLoading: isLoading, name: "Check Key Status", onClick: onCheckKey })),
        !!state.accountGroups?.length && (React.createElement(List, { columns: [
                {
                    name: 'Id',
                    xs: 2,
                    id: 'sid'
                },
                {
                    name: 'Name',
                    xs: 2,
                    id: 'name'
                },
                {
                    xs: 3,
                    name: '',
                    isClickable: false,
                    customRenderValue: ({ id }) => {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: "g-form", style: {
                                    width: '300px',
                                    height: '10px',
                                    padding: '10px 30px 10px 30px'
                                } }, groupKeys[`public.${id}`] && (React.createElement("div", { className: "pointer", onClick: onCopyKey('public', id) },
                                React.createElement("i", { className: "fas fa-copy text-weak u-margin-right-large" }),
                                React.createElement("span", { "data-ph-mask": true }, groupKeys[`public.${id}`])))),
                            React.createElement(ButtonWithLoader, { className: "u-margin-bottom-small", color: "secondary", disabled: false, isLoading: isLoading, name: "Recreate Public Key", onClick: () => {
                                    onUpdateKey('public', id);
                                } })));
                    }
                },
                {
                    xs: 3,
                    name: '',
                    isClickable: false,
                    customRenderValue: ({ id }) => {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: "g-form", style: {
                                    width: '300px',
                                    height: '10px',
                                    padding: '10px 30px 10px 30px'
                                } }, groupKeys[`private.${id}`] && (React.createElement("div", { className: "pointer", onClick: onCopyKey('private', id) },
                                React.createElement("i", { className: "fas fa-copy text-weak u-margin-right-large" }),
                                React.createElement("span", { "data-ph-mask": true }, groupKeys[`private.${id}`])))),
                            React.createElement(ButtonWithLoader, { className: "u-margin-bottom-small", color: "secondary", disabled: false, isLoading: isLoading, name: "Recreate Private Key", onClick: () => {
                                    onUpdateKey('private', id);
                                } })));
                    }
                }
            ], items: state.accountGroups, label: "groups", listSource: "key", loading: isLoading, total: state.accountGroups.length }))));
};
